import React from "react";
import { toast } from "react-toastify";
import { Copy, Download, Clock3, XCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Button } from "../../ui/button";
import { downloadDocument } from "../../../utils/util";
import { retailerTitle, giftCardTypeLabel } from "./GiftCardTile";

const MONO = "ui-monospace, SF Mono, Menlo, Fira Code, monospace";

function handleCopyCode(code) {
  if (!code) {
    toast("No gift card code available", { type: "warning" });
    return;
  }
  navigator.clipboard
    .writeText(code)
    .then(() => toast("Gift card code copied to clipboard!", { type: "success", autoClose: 2000 }))
    .catch(() => toast("Failed to copy code", { type: "error" }));
}

function handleDownloadBarcode(imageBase64, code) {
  try {
    const base64Data = imageBase64.includes("data:") ? imageBase64.split(",")[1] : imageBase64;
    downloadDocument(base64Data, `${code || "barcode"}.png`);
  } catch (error) {
    console.error("Error downloading barcode:", error);
    toast("Unable to download barcode image", { type: "error" });
  }
}

export default function GiftCardRedemptionDialogs({
  selectedCard,
  openStoreCard,
  openCardProcessing,
  openRejectedCard,
  imageBase64,
  onCloseStoreCard,
  onCloseProcessing,
  onCloseRejected,
}) {
  const typeText = giftCardTypeLabel(selectedCard?.gift_card_details?.gift_card_type);

  return (
    <>
      <Dialog open={openStoreCard} onOpenChange={(v) => !v && onCloseStoreCard()}>
        <DialogContent style={{ maxWidth: 440 }} className="member-scroll">
          <DialogHeader>
            <DialogTitle>{retailerTitle(selectedCard?.gift_card_details?.retailer)}</DialogTitle>
          </DialogHeader>

          <div
            style={{
              borderRadius: 14,
              overflow: "hidden",
              border: "1px solid rgba(255,255,255,0.08)",
              background: selectedCard?.bg
                ? `linear-gradient(160deg, ${selectedCard.bg}dd, ${selectedCard.bg}55 70%, transparent)`
                : "linear-gradient(160deg, #2a2a2e, #141416)",
              backgroundColor: selectedCard?.bg || "#1a1a1a",
              padding: "22px 20px",
              textAlign: "center",
            }}
          >
            <img
              src={selectedCard?.logo}
              alt={selectedCard?.retailer}
              style={{ maxWidth: 120, maxHeight: 42, objectFit: "contain", marginBottom: 18 }}
            />
            <div
              style={{
                fontFamily: MONO,
                fontSize: 21,
                fontWeight: 700,
                color: "#fff",
                letterSpacing: "2px",
                marginBottom: 8,
              }}
            >
              {selectedCard?.gift_card_code}
            </div>
            <div style={{ fontSize: 14, color: "rgba(255,255,255,0.75)", letterSpacing: "1px" }}>
              PIN: {selectedCard?.gift_card_pin}
            </div>
            {imageBase64 && (
              <img
                src={imageBase64}
                alt="Barcode"
                style={{ width: 240, height: 90, objectFit: "contain", margin: "16px auto 0", display: "block", borderRadius: 4, background: "#fff" }}
              />
            )}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 18 }}>
              {typeText && (
                <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "rgba(255,255,255,0.65)" }}>
                  {typeText}
                </span>
              )}
              <span style={{ fontFamily: MONO, fontSize: 17, fontWeight: 700, color: "#fff" }}>
                ${selectedCard?.discounted_amount}
              </span>
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
            <Button
              variant="outline"
              style={{ flex: 1 }}
              onClick={() => handleCopyCode(selectedCard?.gift_card_code)}
            >
              <Copy size={14} style={{ marginRight: 6 }} />
              Copy Code
            </Button>
            <Button
              variant="outline"
              style={{ flex: 1 }}
              disabled={!imageBase64}
              onClick={() => handleDownloadBarcode(imageBase64, selectedCard?.gift_card_code)}
            >
              <Download size={14} style={{ marginRight: 6 }} />
              Download Barcode
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={openCardProcessing} onOpenChange={(v) => !v && onCloseProcessing()}>
        <DialogContent style={{ maxWidth: 400 }}>
          <DialogHeader>
            <DialogTitle>{retailerTitle(selectedCard?.gift_card_details?.retailer)}</DialogTitle>
          </DialogHeader>
          <div style={{ textAlign: "center", padding: "12px 4px 4px" }}>
            <img
              src={selectedCard?.logo}
              alt={selectedCard?.retailer}
              style={{ maxWidth: 110, maxHeight: 40, objectFit: "contain", marginBottom: 20 }}
            />
            <div
              style={{
                width: 46,
                height: 46,
                borderRadius: "50%",
                background: "rgba(232,184,75,0.14)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
              }}
            >
              <Clock3 size={22} color="#e8b84b" />
            </div>
            <p style={{ fontSize: 13.5, color: "#c3c8cc", lineHeight: 1.5, margin: "0 0 12px" }}>
              We are processing your request. It might take up to 72hrs to deliver your card details.
            </p>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", borderRadius: 10, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
              {typeText && (
                <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#c3c8cc" }}>
                  {typeText}
                </span>
              )}
              <span style={{ fontFamily: MONO, fontSize: 15, fontWeight: 700, color: "#fff" }}>
                ${selectedCard?.discounted_amount}
              </span>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={openRejectedCard} onOpenChange={(v) => !v && onCloseRejected()}>
        <DialogContent style={{ maxWidth: 400 }}>
          <DialogHeader>
            <DialogTitle>{retailerTitle(selectedCard?.gift_card_details?.retailer)}</DialogTitle>
          </DialogHeader>
          <div style={{ textAlign: "center", padding: "12px 4px 4px" }}>
            <img
              src={selectedCard?.logo}
              alt={selectedCard?.retailer}
              style={{ maxWidth: 110, maxHeight: 40, objectFit: "contain", marginBottom: 20 }}
            />
            <div
              style={{
                width: 46,
                height: 46,
                borderRadius: "50%",
                background: "rgba(242,112,89,0.14)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
              }}
            >
              <XCircle size={22} color="#f27059" />
            </div>
            <p style={{ fontSize: 13.5, color: "#c3c8cc", margin: "0 0 6px" }}>
              This gift card request has been rejected.
            </p>
            {selectedCard?.notes && (
              <p style={{ fontSize: 12.5, color: "#c3c8cc", margin: 0 }}>Reason: {selectedCard.notes}</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
